import React from 'react';
import {
    CATEGORICAL_SLOTS, NEUTRAL_COLOR, asList, asObject, formatOffset, formatSpan, formatTimestamp, playbookLabel,
    pickupResolutionLabel,
} from '../utils/clientDebugFormat';

// Selected logs use the first categorical slot; dropped candidates are hollow and neutral, so selection reads from
// the marker's fill as well as its color.
const SELECTED_COLOR = CATEGORICAL_SLOTS[0];
const DROPPED_COLOR = NEUTRAL_COLOR;

const SOURCE_TAGS = { ENVIRONMENT: 'environment', PLAYBOOK: 'playbook' };

function SourceTag({ source }) {
    if (!source) return null;
    return <span className={`client-debug-source-tag ${String(source).toLowerCase()}`}>{SOURCE_TAGS[source] || source}</span>;
}

const toMs = (value) => {
    const ms = new Date(value).getTime();
    return Number.isNaN(ms) ? null : ms;
};

/** "30 min before to 10 min after the issue · keep 3 before, 3 after per type · wait up to 10 min". */
export function PolicySummary({ policy }) {
    const p = asObject(policy);
    if (p.windowBeforeSeconds == null) return null;
    const sources = asObject(p.sources);
    return (
        <dl className="client-debug-policy">
            <div><dt>Window</dt><dd>{formatSpan(p.windowBeforeSeconds)} before <SourceTag source={sources.windowBefore} />
                {' '}to {formatSpan(p.windowAfterSeconds)} after the issue <SourceTag source={sources.windowAfter} /></dd></div>
            <div><dt>Logs kept per type</dt><dd>{p.maxLogsBefore} before <SourceTag source={sources.maxLogsBefore} />
                {' '}· {p.maxLogsAfter} after <SourceTag source={sources.maxLogsAfter} /></dd></div>
            <div><dt>Wait for logs</dt><dd>up to {formatSpan(p.waitForLogsSeconds)} <SourceTag source={sources.waitForLogs} /></dd></div>
            <div><dt>Index settle</dt><dd>{formatSpan(p.indexSettleSeconds)} after the window closes <SourceTag source={sources.indexSettle} /></dd></div>
        </dl>
    );
}

const WIDTH = 640;
const ROW = 34;
const LEFT = 92;
const RIGHT = 16;
const TOP = 22;

/** One row per log type on a shared time axis: the window, the issue, and every candidate log selected or dropped. */
function PickupStrip({ pickup }) {
    const start = toMs(pickup.windowStart);
    const end = toMs(pickup.windowEnd);
    const issue = toMs(pickup.issueTime);
    const settled = toMs(pickup.settledAt);
    if (start == null || end == null || issue == null || end <= start) return null;
    const axisEnd = Math.max(end, settled || end);
    const x = (ms) => LEFT + ((ms - start) / (axisEnd - start)) * (WIDTH - LEFT - RIGHT);
    const types = asList(pickup.types);
    const height = TOP + Math.max(1, types.length) * ROW + 26;
    const deadline = toMs(pickup.deadline);

    return (
        <svg className="client-debug-pickup-strip" viewBox={`0 0 ${WIDTH} ${height}`} role="img"
            aria-label="Candidate logs around the issue time, selected and dropped">
            <rect x={x(start)} y={TOP - 6} width={x(end) - x(start)} height={types.length * ROW + 4} className="window" rx="4" />
            {settled && settled > end && (
                <rect x={x(end)} y={TOP - 6} width={x(settled) - x(end)} height={types.length * ROW + 4} className="settle" rx="4" />
            )}
            <line x1={x(issue)} x2={x(issue)} y1={TOP - 12} y2={TOP + types.length * ROW} className="issue" />
            <text x={x(issue)} y={TOP - 14} textAnchor="middle" className="label">Issue</text>
            {types.map((type, row) => {
                const y = TOP + row * ROW + ROW / 2 - 4;
                return (
                    <g key={type.type}>
                        <text x={LEFT - 10} y={y + 4} textAnchor="end" className="label">{type.type}</text>
                        <line x1={LEFT} x2={WIDTH - RIGHT} y1={y} y2={y} className="row" />
                        {asList(type.logs).map((log, index) => {
                            const t = toMs(log.time);
                            if (t == null) return null;
                            const cx = Math.min(WIDTH - RIGHT, Math.max(LEFT, x(t)));
                            const tip = `${type.type} ${formatTimestamp(log.time)} (${formatOffset((log.offsetSeconds || 0) * 1000)} from the issue): `
                                + (log.selected ? 'selected' : `dropped, ${log.dropReason === 'OVER_LIMIT' ? 'over the per-side limit' : log.dropReason}`);
                            return (
                                <g key={`${log.time}-${index}`}>
                                    <circle cx={cx} cy={y} r="5" fill={log.selected ? SELECTED_COLOR : '#fff'}
                                        stroke={log.selected ? '#fff' : DROPPED_COLOR} strokeWidth="2" />
                                    <circle cx={cx} cy={y} r="10" fill="transparent"><title>{tip}</title></circle>
                                </g>
                            );
                        })}
                    </g>
                );
            })}
            <text x={x(start)} y={height - 8} className="axis">{formatOffset(start - issue)}</text>
            <text x={x(end)} y={height - 8} textAnchor="middle" className="axis">{formatOffset(end - issue)}</text>
            {settled && settled > end && <text x={x(settled)} y={height - 8} textAnchor="end" className="axis">settled</text>}
            {deadline && deadline <= axisEnd && (
                <line x1={x(deadline)} x2={x(deadline)} y1={TOP - 6} y2={TOP + types.length * ROW - 2} className="deadline" />
            )}
        </svg>
    );
}

/** Why this debug picked up the logs it did: its pickup settings, what the Log Locator found, and when it queued. */
export function LogPickup({ job }) {
    const pickup = asObject(job.pickup);
    const policy = job.pickupPolicy;
    if (!job.pickup && !policy) {
        return <p className="client-debug-muted">No log pickup recorded: the logs came with the request, or the debug predates pickup records.</p>;
    }
    const types = asList(pickup.types);
    const dropped = types.reduce((sum, type) => sum + asList(type.logs).filter((log) => !log.selected).length, 0);
    return (
        <div className="client-debug-pickup">
            <PolicySummary policy={policy} />
            {job.pickup && (
                <>
                    <p className="client-debug-pickup-summary">
                        <strong>{pickupResolutionLabel(pickup.resolution)}.</strong> {pickup.summary}
                        {pickup.checks ? ` Checked ${pickup.checks} time${pickup.checks === 1 ? '' : 's'}, last ${formatTimestamp(pickup.lastCheckAt)}.` : ''}
                        {pickup.resolution === 'WAITING' && pickup.nextCheckAt ? ` Next check ${formatTimestamp(pickup.nextCheckAt)}.` : ''}
                        {pickup.deadline ? ` Deadline ${formatTimestamp(pickup.deadline)}.` : ''}
                    </p>
                    {types.length > 0 && (
                        <>
                            <div className="client-debug-pickup-legend" aria-hidden="true">
                                <span><i className="selected" style={{ background: SELECTED_COLOR }} />Selected</span>
                                <span><i className="dropped" style={{ borderColor: DROPPED_COLOR }} />Dropped (over the per-side limit)</span>
                                <span><i className="window" />Log window</span>
                                <span><i className="settle" />Index settle</span>
                            </div>
                            <PickupStrip pickup={pickup} />
                            <details className="client-debug-pickup-table">
                                <summary>Candidate logs as a table{dropped ? ` (${dropped} dropped)` : ''}</summary>
                                <table className="client-debug-table">
                                    <thead><tr>{['Type', 'Collected', 'From issue', 'Side', 'Index id', 'Result'].map((h) => <th scope="col" key={h}>{h}</th>)}</tr></thead>
                                    <tbody>
                                        {types.flatMap((type) => asList(type.logs).map((log, index) => (
                                            <tr key={`${type.type}-${log.time}-${index}`}>
                                                <td>{type.type}</td>
                                                <td>{formatTimestamp(log.time)}</td>
                                                <td>{formatOffset((log.offsetSeconds || 0) * 1000)}</td>
                                                <td>{log.side === 'AFTER' ? 'After' : 'Before'}</td>
                                                <td>{log.sourceId ?? '—'}</td>
                                                <td>{log.selected ? 'Selected' : 'Dropped: over the limit'}</td>
                                            </tr>
                                        )))}
                                    </tbody>
                                </table>
                                {types.some((type) => type.truncated) && <p className="client-debug-muted">Only the 50 candidates nearest the issue are recorded per type.</p>}
                            </details>
                        </>
                    )}
                </>
            )}
        </div>
    );
}

const yesNo = (value) => (value ? 'Yes' : 'No');

/** The settings this environment runs with, where each pickup value comes from, and the workers serving it. */
export function EnvironmentConfig({ config, loading, error }) {
    const c = asObject(config);
    const execution = asObject(c.execution);
    const locator = asObject(c.locator);
    const upload = asObject(c.upload);
    const workers = asList(c.workers);
    return (
        <section className="client-debug-config" aria-label="Environment configuration">
            <div className="client-debug-section-heading">
                <div>
                    <h2>Environment configuration</h2>
                    <p>Settings in effect for new debugs here. Each debug keeps the pickup settings it was created with.</p>
                </div>
            </div>
            {error && <div className="client-debug-load-error">{error}</div>}
            {loading ? <div className="client-debug-summary-loading">Loading configuration…</div> : !config ? null : (
                <>
                    <div className="client-debug-summary-grid">
                        <div className="client-debug-summary-card">
                            <h3>Execution</h3>
                            <div className="client-debug-summary-values">
                                <div><span>Accepting debugs</span><strong>{yesNo(c.enabled)}</strong></div>
                                <div><span>Longest debug</span><strong>{formatSpan(execution.maxJobDurationSeconds)}</strong></div>
                                <div><span>Worker silent for</span><strong>{formatSpan(execution.workerTimeoutSeconds)}</strong></div>
                                <div><span>No progress for</span><strong>{formatSpan(execution.stallTimeoutSeconds)}</strong></div>
                                <div><span>Attempts per debug</span><strong>{execution.maxAttempts ?? '—'}</strong></div>
                            </div>
                            <p className="client-debug-muted">A debug whose worker goes silent or stops making progress is retried until it runs out of attempts.</p>
                        </div>
                        <div className="client-debug-summary-card">
                            <h3>Log locator</h3>
                            <div className="client-debug-summary-values">
                                <div><span>Index settle</span><strong>{formatSpan(locator.indexSettleSeconds)}</strong></div>
                                <div><span>Checks slow down after</span><strong>{formatSpan(locator.slowCheckAfterSeconds)}</strong></div>
                                <div><span>Then checks every</span><strong>{formatSpan(locator.slowCheckIntervalSeconds)}</strong></div>
                                <div><span>Duplicate window</span><strong>{formatSpan(execution.duplicateWindowSeconds)}</strong></div>
                            </div>
                        </div>
                        <div className="client-debug-summary-card">
                            <h3>Uploads</h3>
                            <div className="client-debug-summary-values">
                                <div><span>Per file</span><strong>{formatBytes(upload.maxFileBytes)}</strong></div>
                                <div><span>Per debug</span><strong>{formatBytes(upload.maxJobUploadBytes)}</strong></div>
                                <div><span>Files per debug</span><strong>{upload.maxFiles ?? '—'}</strong></div>
                            </div>
                        </div>
                    </div>

                    <div className="client-debug-playbook-grid">
                        {asList(c.pickup).map((item) => (
                            <article className="client-debug-playbook-card" key={item.playbook}>
                                <h3>{playbookLabel(item.displayName || item.playbook)}: log pickup</h3>
                                <PolicySummary policy={item.policy} />
                            </article>
                        ))}
                    </div>

                    <div className="client-debug-detail-section">
                        <h4>Workers seen in the last day</h4>
                        {workers.length === 0 ? <p className="client-debug-muted">No worker has claimed a debug in the last day.</p> : (
                            <div className="client-debug-table-wrap">
                                <table className="client-debug-table">
                                    <thead><tr>{['Worker', 'Version', 'Last seen', 'Jobs held', 'Concurrency', 'Max in flight', 'Heartbeat', 'Staging'].map((h) => <th scope="col" key={h}>{h}</th>)}</tr></thead>
                                    <tbody>
                                        {workers.map((worker) => {
                                            const w = asObject(worker.config);
                                            return (
                                                <tr key={worker.workerId}>
                                                    <td><code>{worker.workerId}</code></td>
                                                    <td>{worker.version || '—'}</td>
                                                    <td>{formatTimestamp(worker.lastSeenAt)}</td>
                                                    <td>{worker.activeJobs ?? '—'}</td>
                                                    <td>{w.concurrency ?? '—'}</td>
                                                    <td>{w.maxInFlight ?? '—'}</td>
                                                    <td>{w.heartbeatSeconds != null ? formatSpan(w.heartbeatSeconds) : '—'}</td>
                                                    <td>{w.stagingEnvironment || '—'}{w.stagingRetentionHours != null ? ` · kept ${w.stagingRetentionHours} h` : ''}</td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                </>
            )}
        </section>
    );
}

function formatBytes(value) {
    const bytes = Number(value);
    if (!Number.isFinite(bytes) || bytes <= 0) return '—';
    const gib = bytes / 1024 ** 3;
    if (gib >= 1) return `${Number.isInteger(gib) ? gib : gib.toFixed(1)} GiB`;
    return `${Math.round(bytes / 1024 ** 2)} MiB`;
}
